import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import handleClearError from "../../../core/methods/handleClearError";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import ErrorMessage from "../../../core/common/ErrorMessage";
import webservice_url from "../../../core/webservice/Webservice_url";
import useApi from "../../../core/customhook/useApi";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";

// ── Types ──────────────────────────────────────────────────────────────────────
type PasswordField = "newPassword" | "confirmPassword";

interface ChangePasswordForm {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

const ChangePassword = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ChangePasswordForm>({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [passwordVisibility, setPasswordVisibility] = useState<
    Record<PasswordField, boolean>
  >({
    newPassword: false,
    confirmPassword: false,
  });

  // API
  const { callApi: resetPasswordApi, loading: loading_reset } = useApi<any>(
    webservice_url.reset_password,
    {
      method: "POST",
      autoCall: false,
    },
  );

  // Pre-fill email from local-storage
  useEffect(() => {
    const userMail = localStorage.getItem("user-data");
    if (!!userMail) {
      setFormData((prev) => ({ ...prev, email: userMail }));
    }
  }, []);

  // ── helpers ──────────────────────────────────────────────────────────────────
  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    handleClearError(setErrors, name);
  };

  // ── validation ────────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.newPassword?.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- handleResetPassword ----
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const hashedPassword = CryptoJS.SHA256(formData?.newPassword).toString();

    try {
      const res = await resetPasswordApi({
        email: formData?.email ?? "",
        newPassword: hashedPassword ?? "",
      });
      if (res?.success) {
        toast.success(res?.message || "Reset Successfully");
        navigate(routes.login, { replace: true });
      }
    } catch (error: any) {
      console.error("login failed:", error?.response);
      const errorMsg = error?.response?.data?.error ?? "Invalid Credentials";
      toast.error(errorMsg);
    }
  };
  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid d-flex align-items-center justify-content-center bg-light">
      <div className="w-100" style={{ maxWidth: 600 }}>
        <form className="vh-100" onSubmit={handleResetPassword}>
          <div className="vh-100 d-flex flex-column align-items-center gap-3 px-3 px-sm-4 py-4 pb-0">
            {/* Logo */}
            <div
              className="mx-auto mb-3 d-inline text-center"
              style={{ maxWidth: 190 }}
            >
              <ImageWithBasePath
                src="/assets/img/AupTech.png"
                className="img-fluid"
                alt="Logo"
              />
            </div>

            <div className="card py-4 px-4 px-sm-5 rounded-4 border w-100">
              <div className="text-center mb-3">
                <h2 className="mb-2">Change Password</h2>
                <p className="mb-0">Set a new password for your account</p>
              </div>

              {/* Email — read-only, prefilled from query param */}
              <div className="mb-3">
                <label className="form-label">Email Address</label>
                <div className="input-group mb-1">
                  <input
                    type="email"
                    className="form-control border-end-0"
                    name="email"
                    value={formData.email}
                    disabled
                  />
                  <span className="input-group-text border-start-0">
                    <i className="ti ti-mail" />
                  </span>
                </div>
              </div>

              {/* New Password */}
              <div className="mb-3">
                <label className="form-label">New Password</label>
                <div className="pass-group mb-1">
                  <input
                    type={passwordVisibility.newPassword ? "text" : "password"}
                    className="pass-input form-control"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter new password"
                    disabled={loading_reset}
                  />
                  <span
                    className={`ti toggle-passwords ${
                      passwordVisibility.newPassword ? "ti-eye" : "ti-eye-off"
                    }`}
                    onClick={() => togglePasswordVisibility("newPassword")}
                  />
                </div>
                <ErrorMessage message={errors?.newPassword} />
              </div>

              {/* Confirm Password */}
              <div className="mb-3">
                <label className="form-label">Confirm Password</label>
                <div className="pass-group mb-1">
                  <input
                    type={
                      passwordVisibility.confirmPassword ? "text" : "password"
                    }
                    className="pass-input form-control"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm new password"
                    disabled={loading_reset}
                  />
                  <span
                    className={`ti toggle-passwords ${
                      passwordVisibility.confirmPassword
                        ? "ti-eye"
                        : "ti-eye-off"
                    }`}
                    onClick={() => togglePasswordVisibility("confirmPassword")}
                  />
                </div>
                <ErrorMessage message={errors?.confirmPassword} />
              </div>

              {/* Submit */}
              <div className="mb-3 mt-0">
                <button
                  type="submit"
                  className="btn btn-primary w-100 "
                  disabled={loading_reset}
                >
                  {loading_reset ? "Updating Password…" : "Update Password"}
                </button>
              </div>

              {/* Back to login */}
              <div className="text-center">
                <Link to={all_routes.login} className="link-secondary small">
                  <i className="ti ti-arrow-left" /> Back to Sign In
                </Link>
              </div>
            </div>

            <div className="mt-3 pb-4 text-center">
              <p className="mb-0 text-gray-9">
                © 2026 Aupertz. All rights reserved.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
// import React, { useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { all_routes } from "../../router/all_routes";
// import ImageWithBasePath from "../../../core/common/imageWithBasePath";
// type PasswordField = "password";

// const ResetPassword = () => {
//   const routes = all_routes;
//   const navigation = useNavigate();
//   const navigationPath = () => {
//     navigation(routes.resetPasswordSuccess);
//   };
//   const [passwordVisibility, setPasswordVisibility] = useState({
//     password: false,
//     confirmPassword: false,
//   });
//   const [password, setPassword] = useState("");
//   const [passwordResponce, setPasswordResponce] = useState({
//     passwordResponceText:
//       "Use 8 or more characters with a mix of letters, numbers, and symbols.",
//     passwordResponceKey: "",
//   });

//   const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
//     setPasswordVisibility((prevState) => ({
//       ...prevState,
//       [field]: !prevState[field],
//     }));
//   };

//   const onChangePassword = (password: string) => {
//     setPassword(password);
//     if (password.match(/^$|\s+/)) {
//       setPasswordResponce({
//         passwordResponceText:
//           "Use 8 or more characters with a mix of letters, numbers & symbols",
//         passwordResponceKey: "",
//       });
//     } else if (password.length === 0) {
//       setPasswordResponce({
//         passwordResponceText: "",
//         passwordResponceKey: "",
//       });
//     } else if (password.length < 8) {
//       setPasswordResponce({
//         passwordResponceText: "Weak. Must contain at least 8 characters",
//         passwordResponceKey: "0",
//       });
//     } else if (
//       password.search(/[a-z]/) < 0 ||
//       password.search(/[A-Z]/) < 0 ||
//       password.search(/[0-9]/) < 0
//     ) {
//       setPasswordResponce({
//         passwordResponceText:
//           "Average. Must contain at least 1 upper case and number",
//         passwordResponceKey: "1",
//       });
//     } else if (password.search(/(?=.*?[#?!@$%^&*-])/) < 0) {
//       setPasswordResponce({
//         passwordResponceText: "Almost. Must contain a special symbol",
//         passwordResponceKey: "2",
//       });
//     } else {
//       setPasswordResponce({
//         passwordResponceText: "Awesome! You have a secure password.",
//         passwordResponceKey: "3",
//       });
//     }
//   };

//   return (
//     <div className="container-fuild">
//       <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
//         <div className="row">
//           <div className="col-lg-5">
//             <div className="login-background position-relative d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100">
//               <div className="bg-overlay-img">
//                 <ImageWithBasePath
//                   src="assets/img/bg/bg-01.png"
//                   className="bg-1"
//                   alt="Img"
//                 />
//                 <ImageWithBasePath
//                   src="assets/img/bg/bg-02.png"
//                   className="bg-2"
//                   alt="Img"
//                 />
//                 <ImageWithBasePath
//                   src="assets/img/bg/bg-03.png"
//                   className="bg-3"
//                   alt="Img"
//                 />
//               </div>
//               <div className="authentication-card w-100">
//                 <div className="authen-overlay-item border w-100">
//                   <h1 className="text-white display-1">
//                     Empowering people <br /> through seamless HR <br />{" "}
//                     management.
//                   </h1>
//                   <div className="my-4 mx-auto authen-overlay-img">
//                     <ImageWithBasePath
//                       src="assets/img/bg/authentication-bg-01.png"
//                       alt="Img"
//                     />
//                   </div>
//                   <div>
//                     <p className="text-white fs-20 fw-semibold text-center">
//                       Efficiently manage your workforce, streamline <br />{" "}
//                       operations effortlessly.
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="col-lg-7 col-md-12 col-sm-12">
//             <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap">
//               <div className="col-md-7 mx-auto vh-100">
//                 <form action="success.html" className="vh-100">
//                   <div className="vh-100 d-flex flex-column justify-content-between p-4 pb-0">
//                     <div className=" mx-auto mb-5 text-center">
//                       <ImageWithBasePath
//                         src="assets/img/AupTech.svg"
//                         className="img-fluid"
//                         alt="Logo"
//                       />
//                     </div>
//                     <div className="">
//                       <div className="text-center mb-3">
//                         <h2 className="mb-2">Reset Password</h2>
//                         <p className="mb-0">
//                           Your new password must be different from previous used
//                           passwords.
//                         </p>
//                       </div>
//                       <div>
//                         <div className="input-block mb-3">
//                           <div className="mb-3">
//                             <label className="form-label">Password</label>
//                             <div className="pass-group" id="passwordInput">
//                               <input
//                                 type={
//                                   passwordVisibility.password
//                                     ? "text"
//                                     : "password"
//                                 }
//                                 value={password}
//                                 onChange={(e) =>
//                                   onChangePassword(e.target.value)
//                                 }
//                                 className="form-control pass-input"
//                                 placeholder="Enter your password"
//                               />
//                               <span
//                                 className={`ti toggle-passwords ${
//                                   passwordVisibility.password
//                                     ? "ti-eye"
//                                     : "ti-eye-off"
//                                 }`}
//                                 onClick={() =>
//                                   togglePasswordVisibility("password")
//                                 }
//                                 style={{ cursor: "pointer" }}
//                               ></span>
//                             </div>
//                           </div>
//                           <div
//                             className={`password-strength d-flex ${
//                               passwordResponce.passwordResponceKey === "0"
//                                 ? "poor-active"
//                                 : passwordResponce.passwordResponceKey === "1"
//                                   ? "avg-active"
//                                   : passwordResponce.passwordResponceKey === "2"
//                                     ? "strong-active"
//                                     : passwordResponce.passwordResponceKey ===
//                                         "3"
//                                       ? "heavy-active"
//                                       : ""
//                             }`}
//                             id="passwordStrength"
//                           >
//                             <span id="poor" className="active" />
//                             <span id="weak" className="active" />
//                             <span id="strong" className="active" />
//                             <span id="heavy" className="active" />
//                           </div>
//                         </div>
//                         <p className="fs-12">
//                           {passwordResponce.passwordResponceText}
//                         </p>
//                         <div className="mb-3">
//                           <label className="form-label">Confirm Password</label>
//                           <div className="pass-group">
//                             <input
//                               type={
//                                 passwordVisibility.confirmPassword
//                                   ? "text"
//                                   : "password"
//                               }
//                               className="pass-input form-control"
//                             />
//                             <span
//                               className={`ti toggle-passwords ${
//                                 passwordVisibility.confirmPassword
//                                   ? "ti-eye"
//                                   : "ti-eye-off"
//                               }`}
//                               onClick={() =>
//                                 togglePasswordVisibility("confirmPassword")
//                               }
//                             ></span>
//                           </div>
//                         </div>
//                         <div className="mb-3">
//                           <button
//                             type="submit"
//                             onClick={navigationPath}
//                             className="btn btn-primary w-100"
//                           >
//                             Submit
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="mt-5 pb-4 text-center">
//                       <p className="mb-0 text-gray-9">
//                         © 2026 Aupertz. All rights reserved.
//                       </p>
//                     </div>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ResetPassword;
